import React, { useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import {
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  MenuItem,
} from "@material-ui/core";
import { Add, Edit, Delete, Close, Remove, Save } from "@material-ui/icons";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import { useTranslation } from "react-i18next";
import useThemeColors from "hooks/useThemeColors";
import {
  grayColor,
  whiteColor,
  blendColors,
} from "assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => ({
  // Container
  plWrapper: {
    position: "relative",
    marginTop: "15px",
  },
  // Header
  plHeader: {
    color: theme.palette.custom?.clientTextColor || whiteColor,
    borderRadius: "5px 5px 0 0",
    paddingLeft: "10px",
    paddingRight: "10px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "500",
    paddingTop: "6px",
    paddingBottom: "6px",
    marginBottom: "0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.custom?.clientColor || grayColor[2],
  },
  headerTitle: {
    fontSize: "13px",
    fontWeight: "500",
  },
  headerActions: {
    display: "flex",
    gap: "5px",
  },
  addButton: {
    color: theme.palette.custom?.clientTextColor || whiteColor,
    padding: "4px",
    minHeight: "unset",
    "& svg": {
      fontSize: "16px",
    },
  },
  // Lines wrapper
  plOuterWrapper: {
    width: "100%",
    padding: "0",
    display: "inline-block",
    fontSize: "12px",
    color: grayColor[6],
    whiteSpace: "nowrap",
    overflow: "hidden",
    border: `1px solid ${grayColor[8]}`,
    borderTop: "none",
    borderRadius: "0 0 5px 5px",
    backgroundColor: whiteColor,
  },
  // Single line
  plLineWrapper: {
    width: "100%",
    minHeight: "32px",
    display: "flex",
    alignItems: "center",
    lineHeight: "1.5",
    padding: "0 4px",
    transition: "all 0.15s ease",
    borderBottom: `1px solid ${grayColor[9]}`,
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: grayColor[10],
      "& $lineActions": {
        opacity: 1,
      },
    },
  },
  plTotal: {
    backgroundColor: grayColor[8],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "11px",
  },
  plSubTotal: {
    backgroundColor: grayColor[11],
    fontWeight: "500",
    "& $plLineDescription": {
      paddingLeft: "20px",
    },
  },
  plLevel3: {
    color: grayColor[1],
    "& $plLineDescription": {
      paddingLeft: "40px",
    },
  },
  highlighted: {
    backgroundColor: (props) =>
      blendColors(grayColor[8], props.clientColor || grayColor[2], 0.85) +
      " !important",
  },
  // Line description
  plLineDescription: {
    flex: 1,
    paddingLeft: "8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  lineName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  // Drill down toggle button
  drillDownButton: {
    padding: "2px",
    marginRight: "4px",
    cursor: "pointer",
    "& svg": {
      fontSize: "14px",
    },
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.08)",
      borderRadius: "3px",
    },
  },
  // Line actions
  lineActions: {
    display: "flex",
    gap: "2px",
    opacity: 0,
    transition: "opacity 0.15s ease",
  },
  actionIcon: {
    padding: "4px",
    "& svg": {
      fontSize: "16px",
    },
  },
  // Empty state
  emptyState: {
    padding: "40px 20px",
    textAlign: "center",
    color: grayColor[3],
    fontSize: "14px",
  },
  // Hidden
  hidden: {
    display: "none",
  },
  // Dialog
  dialogContent: {
    paddingTop: "10px",
  },
}));

function PLLine({
  line,
  onEdit,
  onDelete,
  onAddChild,
  drillDownsOpen,
  onDrillDownClick,
  hoveredLine,
  setHoveredLine,
}) {
  const { clientColor } = useThemeColors();
  const classes = useStyles({ clientColor });
  const { t } = useTranslation();

  const hasChildren = line.is_parent;
  const isOpen = drillDownsOpen.includes(line.id);
  const isHovered = hoveredLine === line.id;

  const handleDrillDownClick = (e) => {
    e.stopPropagation();
    onDrillDownClick(line.id);
  };

  return (
    <>
      <div
        className={cx({
          [classes.plLineWrapper]: true,
          [classes.plTotal]: line.level === 1,
          [classes.plSubTotal]: line.level === 2,
          [classes.plLevel3]: line.level >= 3,
          [classes.highlighted]: isHovered,
        })}
        onMouseEnter={() => setHoveredLine(line.id)}
        onMouseLeave={() => setHoveredLine(null)}
      >
        <div className={classes.plLineDescription}>
          {hasChildren && (
            <span
              className={classes.drillDownButton}
              onClick={handleDrillDownClick}
            >
              {isOpen ? <Remove /> : <Add />}
            </span>
          )}
          <span className={classes.lineName}>{line.name}</span>
        </div>

        <div className={classes.lineActions}>
          {/* Only show add child button if level < 5 (max 5 levels) */}
          {line.level < 5 && (
            <Tooltip title={t("ctsguide.pl.add_child")}>
              <IconButton
                className={classes.actionIcon}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddChild(line);
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t("common.edit")}>
            <IconButton
              className={classes.actionIcon}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(line);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <IconButton
              className={classes.actionIcon}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(line.id);
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Render children if expanded */}
      {hasChildren &&
        isOpen &&
        line.children?.map((child) => (
          <PLLine
            key={child.id}
            line={child}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddChild={onAddChild}
            drillDownsOpen={drillDownsOpen}
            onDrillDownClick={onDrillDownClick}
            hoveredLine={hoveredLine}
            setHoveredLine={setHoveredLine}
          />
        ))}
    </>
  );
}

PLLine.propTypes = {
  line: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddChild: PropTypes.func.isRequired,
  drillDownsOpen: PropTypes.array.isRequired,
  onDrillDownClick: PropTypes.func.isRequired,
  hoveredLine: PropTypes.string,
  setHoveredLine: PropTypes.func.isRequired,
};

export default function PLTree({ lines, onAdd, onEdit, onDelete }) {
  const { clientColor } = useThemeColors();
  const classes = useStyles({ clientColor });
  const { t } = useTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    parent: null,
    level: 1,
  });
  const [drillDownsOpen, setDrillDownsOpen] = useState([]);
  const [hoveredLine, setHoveredLine] = useState(null);
  // Track if parent was set via handleAddChild (auto) vs user selection
  const [parentSetByChild, setParentSetByChild] = useState(false);

  // Flatten lines to get all possible parent lines (level 2+) for parent dropdown
  const getParentCandidateLines = React.useCallback(() => {
    const result = [];
    const flatten = (lineList) => {
      lineList?.forEach((line) => {
        if (line.level >= 2) {
          result.push(line);
        }
        if (line.children) {
          flatten(line.children);
        }
      });
    };
    flatten(lines);
    return result;
  }, [lines]);

  const handleAddNew = () => {
    setEditingLine(null);
    setFormData({
      name: "",
      parent: null,
      level: 1,
    });
    setParentSetByChild(false);
    setDialogOpen(true);
  };

  const handleAddChild = (parentLine) => {
    setEditingLine(null);
    setFormData({
      name: "",
      parent: parentLine.id,
      level: parentLine.level + 1,
    });
    setParentSetByChild(true);
    setDialogOpen(true);
  };

  const handleEdit = (line) => {
    setEditingLine(line);
    setFormData({
      name: line.name,
      parent: line.parent,
      level: line.level,
    });
    setParentSetByChild(!!line.parent);
    setDialogOpen(true);
  };

  // Handle level change - show/hide parent dropdown
  const handleLevelChange = (newLevel) => {
    if (newLevel >= 3 && !parentSetByChild) {
      // Level 3+ selected from header +, keep parent selection open
      setFormData({ ...formData, level: newLevel });
    } else if (newLevel < 3 && !parentSetByChild) {
      // Changed to level 1 or 2, clear parent if it was manually set
      setFormData({ ...formData, level: newLevel, parent: null });
    } else {
      setFormData({ ...formData, level: newLevel });
    }
  };

  const handleSave = () => {
    if (!formData.name.trim()) return;

    if (editingLine) {
      onEdit(editingLine.id, formData);
    } else {
      onAdd(formData);
    }
    setDialogOpen(false);
  };

  const handleDrillDownClick = (lineId) => {
    if (drillDownsOpen.includes(lineId)) {
      setDrillDownsOpen(drillDownsOpen.filter((id) => id !== lineId));
    } else {
      setDrillDownsOpen([...drillDownsOpen, lineId]);
    }
  };

  // Initialize all parent lines as open
  React.useEffect(() => {
    const parentIds = [];
    const findParents = (lineList) => {
      lineList?.forEach((line) => {
        if (line.is_parent) {
          parentIds.push(line.id);
          findParents(line.children);
        }
      });
    };
    findParents(lines);
    setDrillDownsOpen(parentIds);
  }, [lines]);

  return (
    <div className={classes.plWrapper}>
      {/* Header */}
      <div className={classes.plHeader}>
        <span className={classes.headerTitle}>
          {t("ctsguide.pl.structure_title")}
        </span>
        <div className={classes.headerActions}>
          <Tooltip title={t("ctsguide.pl.add_line")}>
            <span>
              <Button
                size="sm"
                justIcon
                className={classes.addButton}
                onClick={handleAddNew}
              >
                <Add />
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Lines */}
      <div className={classes.plOuterWrapper}>
        {lines && lines.length > 0 ? (
          lines.map((line) => (
            <PLLine
              key={line.id}
              line={line}
              onEdit={handleEdit}
              onDelete={onDelete}
              onAddChild={handleAddChild}
              drillDownsOpen={drillDownsOpen}
              onDrillDownClick={handleDrillDownClick}
              hoveredLine={hoveredLine}
              setHoveredLine={setHoveredLine}
            />
          ))
        ) : (
          <div className={classes.emptyState}>
            {t("ctsguide.pl.no_lines")}
            <br />
            <Tooltip title={t("ctsguide.pl.add_first_line")}>
              <span>
                <Button
                  color="client"
                  size="sm"
                  justIcon
                  onClick={handleAddNew}
                  style={{ marginTop: "15px" }}
                >
                  <Add />
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Edit/Add Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <ModalHeader
          title={
            editingLine ? t("ctsguide.pl.edit_line") : t("ctsguide.pl.add_line")
          }
          subtitle={
            parentSetByChild && formData.parent
              ? t("ctsguide.pl.adding_as_child")
              : undefined
          }
          onClose={() => setDialogOpen(false)}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent className={classes.dialogContent}>
          <TextField
            fullWidth
            autoFocus
            label={t("ctsguide.pl.line_name")}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            variant="outlined"
            placeholder={t("ctsguide.pl.line_name_placeholder")}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
          />

          <TextField
            select
            fullWidth
            label={t("ctsguide.pl.level")}
            value={formData.level}
            onChange={(e) => handleLevelChange(parseInt(e.target.value, 10))}
            margin="normal"
            variant="outlined"
            disabled={parentSetByChild}
          >
            <MenuItem value={1}>{t("ctsguide.pl.level_1")}</MenuItem>
            <MenuItem value={2}>{t("ctsguide.pl.level_2")}</MenuItem>
            <MenuItem value={3}>{t("ctsguide.pl.level_3")}</MenuItem>
            <MenuItem value={4}>{t("ctsguide.pl.level_4", "Level 4")}</MenuItem>
            <MenuItem value={5}>{t("ctsguide.pl.level_5", "Level 5")}</MenuItem>
          </TextField>

          {/* Show parent dropdown when level 3+ is selected from header + */}
          {formData.level >= 3 && !parentSetByChild && (
            <TextField
              select
              fullWidth
              label={t("ctsguide.pl.parent_line")}
              value={formData.parent || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  parent: e.target.value || null,
                })
              }
              margin="normal"
              variant="outlined"
            >
              <MenuItem value="">{t("common.none")}</MenuItem>
              {getParentCandidateLines().map((line) => (
                <MenuItem key={line.id} value={line.id}>
                  {line.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {parentSetByChild && formData.parent && (
            <p
              style={{
                fontSize: "12px",
                color: grayColor[3],
                marginTop: "8px",
              }}
            >
              {t("ctsguide.pl.adding_as_child")}
            </p>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Tooltip title={t("common.cancel")}>
            <span>
              <Button
                size="sm"
                justIcon
                color="system_gray"
                onClick={() => setDialogOpen(false)}
              >
                <Close />
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t("common.save")}>
            <span>
              <Button
                size="sm"
                justIcon
                color="client"
                onClick={handleSave}
                disabled={!formData.name.trim()}
              >
                <Save />
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </div>
  );
}

PLTree.propTypes = {
  lines: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
